<template>
    <switch-theme-message />

    <LaNoData
        v-if="selectedMode === 'yield'"
        header="Install not available for Yield"
        description="The install steps are only available for LTBA publishers."
    />

    <template v-else>
        <LaPageHeader v-if="selectedWebsite" :heading="'Install / ' + selectedWebsite.domain">
            <template #controls v-if="filteredWebsites?.length > 1">
                <LaWebsitePicker v-model="selectedWebsite" :websites="filteredWebsites" />
            </template>
        </LaPageHeader>

        <LaCardLoader v-if="loading" />
        <LaNoData
            v-else-if="websites && !websites.length"
            header="Add website"
            description="Please add website to show install steps for it."
        >
            <LaAddWebsite />
        </LaNoData>
        <LaInstallSteps v-else-if="selectedWebsite" :website="selectedWebsite" />
    </template>
</template>

<script>
import LaInstallSteps from "@/components/install/InstallSteps";
import LaPageHeader from "@/components/PageHeader";
import LaAddWebsite from "@/components/button/AddWebsite";
import LaWebsitePicker from "@/components/filters/WebsitePicker";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "@/store/store";
import { actionTypes as websiteActionTypes, getterTypes as websiteGetterTypes } from "@/store/Websites/types";
import { actionTypes as publisherActionTypes, getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import LaNoData from "@/components/NoData";
import LaCardLoader from "@/components/loaders/CardLoader";
import SwitchThemeMessage from "@/components/SwitchThemeMessage.vue";

export default {
    name: "LaInstallView",
    components: {
        LaWebsitePicker,
        LaInstallSteps,
        LaPageHeader,
        LaAddWebsite,
        LaNoData,
        LaCardLoader,
        SwitchThemeMessage,
    },
    data() {
        return {
            paramWebsite: this.$route.params.website ? this.$route.params.website : null,
        };
    },
    setup() {
        const route = useRoute();
        const paramPublisher = route.params.publisher;

        // Set LTBA publisher from URL parameter if provided — install is LTBA-only.
        // Look up the full publisher object once the list is loaded.
        if (paramPublisher) {
            const syncLtbaFromRoute = () => {
                const all = store.getters[publisherGetterTypes.getPublishers] || [];
                const match = all.find((p) => p?.id?.toString() === paramPublisher.toString());
                if (match && !match.is_yield) {
                    const current = store.getters[publisherGetterTypes.getSelectedPublisherLtba];
                    if (!current || current.id !== match.id) {
                        store.dispatch(publisherActionTypes.setSelectedPublisherLtba, match);
                    }
                    if (store.getters[publisherGetterTypes.getSelectedMode] !== "ltba") {
                        store.dispatch(publisherActionTypes.setSelectedMode, "ltba");
                    }
                }
            };
            if (store.getters[publisherGetterTypes.getPublishers]) {
                syncLtbaFromRoute();
            } else {
                store.dispatch(publisherActionTypes.getData).then(syncLtbaFromRoute);
            }
        }

        const selectedMode = computed(() => store.getters[publisherGetterTypes.getSelectedMode]);
        const selectedPublisherLtba = computed(() => store.getters[publisherGetterTypes.getSelectedPublisherLtba]);
        const websites = computed(() => store.getters[websiteGetterTypes.getWebsitesByPublisher]);
        const filteredWebsites = computed(() => {
            if (!websites.value) return [];
            return websites.value.filter((w) => w.id !== "all");
        });

        // Load websites when publisher is selected
        const loadWebsites = (publisherId) => {
            if (publisherId) {
                store
                    .dispatch(websiteActionTypes.getDataByPublisher, { publisherId })
                    .then(() => (selectedWebsite.value = defaultSelected.value));
            }
        };

        // Initial load
        if (selectedPublisherLtba.value?.id) {
            loadWebsites(selectedPublisherLtba.value.id);
        }

        // Watch for LTBA publisher changes
        watch(selectedPublisherLtba, (newPublisher) => {
            if (newPublisher?.id) {
                loadWebsites(newPublisher.id);
            }
        });

        const defaultSelected = computed(() => {
            if (!websites.value || websites.value.length === 0) return null;

            // Filter out "all" item
            const actualWebsites = websites.value.filter((w) => w.id !== "all");
            const paramWebsite = route.params.website;

            if (paramWebsite) {
                const websiteId = paramWebsite.toString();
                return (
                    actualWebsites.find(
                        (i) => i.aab_site_id?.toString() === websiteId || i.id?.toString() === websiteId
                    ) || null
                );
            } else {
                // Return first unverified website, or first website if all are verified
                return actualWebsites.find(({ verified }) => !verified) || actualWebsites[0] || null;
            }
        });

        const selectedWebsite = ref(defaultSelected.value);
        const loading = computed(() => store.getters[websiteGetterTypes.isLoading]);
        const router = useRouter();

        // Watch for changes in defaultSelected and update selectedWebsite automatically
        watch(defaultSelected, (newDefault) => {
            if (newDefault) {
                selectedWebsite.value = newDefault;
            }
        });

        watch(
            [selectedWebsite],
            () => {
                if (selectedWebsite.value) {
                    document.title = "AAB Install | " + selectedWebsite.value.site_name;
                }
                if ((!route.params || !route.params.website) && selectedWebsite.value) {
                    router.push({ params: { website: selectedWebsite.value.aab_site_id } });
                }
            },
            { immediate: true }
        );

        return { loading, websites, selectedWebsite, filteredWebsites, selectedMode };
    },
};
</script>
